import React, { useState } from "react";
import { Button, Col, message, Row, Table } from "antd";
import { EditOutlined } from "@ant-design/icons";
import { useParams } from "react-router-dom";

import { patchAdviseeCashFlowInQuickManage } from "apis/advisee";
import {
  constructDisplayPrice,
  constructDisplayPercentage,
} from "utils/general";

import InfoCard from "components/InfoCard/InfoCard";
import Title from "components/Title/Title";
import SectionContainer from "components/SectionContainer/SectionContainer";

import QuickManageModal from "./components/QuickManageModal/QuickManageModal";
import {
  SummaryValueText,
  DescriptionForLabel,
  FinancialValueText,
  Label,
} from "./Summary.styles";

import Locale from "react-intl-universal";

const constructCashFlowManagementDataSource = (summaryInfo) => [
  {
    label: Locale.get("cashFlowSummary.tCashInflow"),
    value: constructDisplayPrice(summaryInfo.totalInflow || 0, false),
  },
  {
    label: Locale.get("cashFlowSummary.tCashOutflow"),
    desc: Locale.get("cashFlowSummary.outFlowDesc"),
    value: constructDisplayPrice(summaryInfo.totalOutflow || 0, false),
  },
  {
    label: Locale.get("cashFlowSummary.expIncome"),
    value: constructDisplayPercentage(summaryInfo.strengthIndicator) || "-",
  },
];

const constructIndividualCashFlowDataSource = (values, type) => {
  const data = {
    expenses: values.outflow[type],
    income: values.inflow[type],
  };

  return [
    {
      label: Locale.get("cashFlowSummary.tCashInflow"),
      value: constructDisplayPrice(data.income || 0, false),
    },
    {
      label: Locale.get("cashFlowSummary.tCashOutflow"),
      desc: type === "self" && Locale.get("cashFlowSummary.outFlowDesc"),
      value: constructDisplayPrice(data.expenses || 0, false),
    },
  ];
};

const cashFlowManagementTableColumns = [
  {
    dataIndex: "label",
    key: "label",
    render: (value, record) => (
      <div>
        <Label>{value}</Label>
        <DescriptionForLabel>{record.desc}</DescriptionForLabel>
      </div>
    ),
  },
  {
    dataIndex: "value",
    key: "value",
    render: (value) => <FinancialValueText>{value}</FinancialValueText>,
  },
];

const getIndividualCashFlow = (title) => [
  {
    title,
    dataIndex: "label",
    key: "label",
    render: (value, record) => (
      <div>
        <Label>{value}</Label>
        <DescriptionForLabel>{record.desc}</DescriptionForLabel>
      </div>
    ),
  },
  {
    dataIndex: "value",
    key: "value",
    render: (value) => <FinancialValueText>{value}</FinancialValueText>,
  },
];

const Summary = ({ summaryInfo = {} }) => {
  const { id: adviseeId } = useParams();
  const [showQuickManage, setShowQuickManageModal] = useState(false);

  const handleOnQuickManageModalOkButtonClick = (payload) => {
    patchAdviseeCashFlowInQuickManage(adviseeId, payload)
      .then((res) => {
        message.success(Locale.get("cashFlowSummary.updateSuccess"));
      })
      .catch((ex) => {
        message.error(Locale.get("cashFlowSummary.summaryError"));
      });
  };

  return (
    <>
      <Title marginBottom="24px">
        {Locale.get("cashFlowSummary.titleCashFlowSummary")}
      </Title>
      <SectionContainer>
        <Row gutter={[16, 16]}>
          <Col span={12} xl={8}>
            <InfoCard title={Locale.get("cashFlowSummary.netCashFlow")}>
              <SummaryValueText>
                {constructDisplayPrice(summaryInfo.nettCashFlow || 0, false)}
              </SummaryValueText>
            </InfoCard>
          </Col>
          <Col span={12} xl={8}>
            <InfoCard title={Locale.get("cashFlowSummary.bLiquidFund")}>
              <SummaryValueText>
                {constructDisplayPrice(summaryInfo.liquidityFunds || 0, false)}
              </SummaryValueText>
            </InfoCard>
          </Col>
          <Col span={12} xl={8}>
            <InfoCard
              title={Locale.get("cashFlowSummary.monthlyIncomeSIndicator")}
            >
              <SummaryValueText>
                {constructDisplayPrice(
                  summaryInfo.incomeStrengthIndicator || 0,
                  false
                )}
              </SummaryValueText>
            </InfoCard>
          </Col>
        </Row>
      </SectionContainer>
      <SectionContainer>
        <Row
          justify="space-between"
          align="middle"
          style={{ marginBottom: "16px", width: "100%" }}
        >
          <Title>{Locale.get("cashFlowSummary.cashFlow")}</Title>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => setShowQuickManageModal(true)}
          >
            {Locale.get("cashFlowSummary.quickManageButton")}
          </Button>
        </Row>
        <SectionContainer>
          <Row>
            <Table
              dataSource={constructCashFlowManagementDataSource(summaryInfo)}
              columns={cashFlowManagementTableColumns}
              pagination={false}
              rowKey={(record) => record.label}
              showHeader={false}
              style={{ width: "100%" }}
            />
          </Row>
        </SectionContainer>
        <SectionContainer>
          <Row>
            <Table
              dataSource={constructIndividualCashFlowDataSource(
                summaryInfo,
                "self"
              )}
              columns={getIndividualCashFlow(
                Locale.get("cashFlowSummary.self")
              )}
              pagination={false}
              rowKey={(record) => record.label}
              style={{ width: "100%" }}
            />
          </Row>
        </SectionContainer>
        <Row>
          <Table
            dataSource={constructIndividualCashFlowDataSource(
              summaryInfo,
              "spouse"
            )}
            columns={getIndividualCashFlow(
              Locale.get("cashFlowSummary.spouse")
            )}
            pagination={false}
            rowKey={(record) => record.label}
            style={{ width: "100%" }}
          />
        </Row>
      </SectionContainer>
      {showQuickManage && (
        <QuickManageModal
          defaultValues={summaryInfo}
          onOk={handleOnQuickManageModalOkButtonClick}
          onCancel={() => setShowQuickManageModal(false)}
          visible={showQuickManage}
        />
      )}
    </>
  );
};

export default Summary;
